import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Stack, Typography, Button, TextField, Paper, Divider } from '@mui/material';
import TimeZoneSettings from '../../components/calendar/TimeZoneSettings';
import CalendarList from '../../components/calendar/CalendarList';
import { useAuthStore, useCalendarAuthStore } from '../../stores/root_store';

const CalendarSettingsTab = observer(() => {
  const authStore = useAuthStore();
  const calendarAuthStore = useCalendarAuthStore();

  const [calendarPreferences, setCalendarPreferences] = useState('');
  const [savedPreferences, setSavedPreferences] = useState('');
  const [saving, setSaving] = useState(false);

  const identity = authStore.user?.uuid || '';

  useEffect(() => {
    calendarAuthStore.fetchCalendarPreferences().then((prefs) => {
      if (prefs) {
        setCalendarPreferences(prefs);
        setSavedPreferences(prefs);
      }
    });
  }, [calendarAuthStore]);

  async function handleSignIn() {
    if (!identity) return;

    const authUrl = await calendarAuthStore.fetchAuthUrl(identity);
    if (authUrl) {
      window.location.href = authUrl;
    }
  }

  async function savePreferences() {
    setSaving(true);
    try {
      await calendarAuthStore.setCalendarPreferences(calendarPreferences);
      setSavedPreferences(calendarPreferences);
    } finally {
      setSaving(false);
    }
  }

  async function handleDisconnect() {
    if (!identity) return;
    if (!confirm('Are you sure you want to disconnect your Google Calendar?')) return;

    const success = await calendarAuthStore.disconnectCalendar(identity);
    if (success) {
      window.location.reload();
    }
  }

  return (
    <Stack spacing={3} p={3}>
      <TimeZoneSettings />

      <Divider />

      {!authStore.user?.has_calendar && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Sync your Google Calendar</Typography>
            <Typography variant="body2" color="text.secondary">
              Connect your Google Calendar to enable scheduling features.
            </Typography>
            <Button variant="contained" onClick={handleSignIn}>
              Connect Google Calendar
            </Button>
          </Stack>
        </Paper>
      )}

      {authStore.user?.has_calendar && (
        <>
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Select Calendars</Typography>
              <Typography variant="body2" color="text.secondary">
                Choose which calendars to sync.
                {authStore.isClient && (
                  <> Your assistants will be able to see the selected calendars when scheduling appointments.</>
                )}
              </Typography>
              <CalendarList identity={identity} />
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Scheduling Preferences</Typography>
              <Typography variant="body2" color="text.secondary">
                Any notes we should know when scheduling appointments?
              </Typography>
              <TextField
                multiline
                rows={3}
                placeholder="Example: No lunch meetings please..."
                value={calendarPreferences}
                onChange={(e) => setCalendarPreferences(e.target.value)}
                fullWidth
              />
              <Button
                variant="contained"
                disabled={calendarPreferences === savedPreferences || saving}
                onClick={savePreferences}
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Disconnect Calendar</Typography>
              <Typography variant="body2" color="text.secondary">
                Remove the connection to your Google Calendar. You can reconnect at any time.
              </Typography>
              <Button variant="outlined" color="error" onClick={handleDisconnect}>
                Disconnect Google Calendar
              </Button>
            </Stack>
          </Paper>
        </>
      )}
    </Stack>
  );
});

export default CalendarSettingsTab;
